import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import { RANKED_PPP_PARAMS, usePpps } from "@/hooks/usePpps";
import { usePppReorder } from "@/hooks/usePppReorder";
import { Ppp as PppModel, PppCategory, PppFilter, PppType } from "@/types";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeaderFrame } from "@/components/layout/page-header-frame";
import { PppFilters } from "@/components/ppp/PppFilters";
import { PppList } from "@/components/ppp/PppList";
import { PppFormDialog } from "@/components/ppp/PppFormDialog";
import { PppDeleteDialog } from "@/components/ppp/PppDeleteDialog";
import { Button } from "@/components/ui/button";
import { Layers, Plus } from "lucide-react";

const Ppp = () => {
  // Leaders create and edit PPPs; ranking and deleting them is director-level, like OKR writes.
  const { canEditOkr, canViewOkr } = useAuth();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [type, setType] = useState<PppType | "">("");
  const [category, setCategory] = useState<PppCategory | "">("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PppModel | null>(null);
  const [deleting, setDeleting] = useState<PppModel | null>(null);

  const params = useMemo<PppFilter>(() => ({
    ...RANKED_PPP_PARAMS,
    search: debouncedSearch || undefined,
    type: type || undefined,
    category: category || undefined,
  }), [debouncedSearch, type, category]);
  const hasFilters = !!(debouncedSearch || type || category);

  const query = usePpps(params);
  const ppps = useMemo(() => query.data?.data ?? [], [query.data?.data]);
  // A full permutation needs the full list; never rank a truncated page.
  const truncated = (query.data?.total ?? 0) > ppps.length;
  const { onDragEnd, isPending: reorderPending } = usePppReorder(ppps, params);

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (ppp: PppModel) => { setEditing(ppp); setFormOpen(true); };

  return (
    <PageShell>
      <PageHeaderFrame
        icon={Layers}
        title="PPP"
        description={
          <>
            Products, Partners, Projects and Company work — the only hand-ranked layer.
            {canEditOkr ? " Drag to rank; every deliverable inherits this order." : " Deliverables inherit this order."}
          </>
        }
        right={
          canViewOkr ? (
            <Button variant="gradient" className="shrink-0 gap-2 rounded-full px-6 shadow-sm" onClick={openCreate}>
              <Plus className="h-4 w-4" /> New PPP
            </Button>
          ) : null
        }
      />

      <PppFilters
        search={search} onSearchChange={setSearch}
        type={type} onTypeChange={setType}
        category={category} onCategoryChange={setCategory}
      />

      <PppList
        ppps={ppps}
        isLoading={query.isLoading}
        isError={query.isError}
        canEdit={canViewOkr}
        canRank={canEditOkr}
        hasFilters={hasFilters || truncated || reorderPending}
        showRank={!hasFilters && !truncated}
        onDragEnd={onDragEnd}
        onEdit={openEdit}
        onDelete={setDeleting}
      />

      {formOpen && <PppFormDialog key={editing?.pid ?? "new"} open onOpenChange={setFormOpen} ppp={editing} />}
      <PppDeleteDialog ppp={deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }} />
    </PageShell>
  );
};

export default Ppp;
